'use client';

import { IconBookmark, IconBookmarkFilled, IconPencil } from '@tabler/icons-react';
import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import type { CalcBookmarkListEntry, CalcHistoryListEntry } from '@/app/app-api/calc-params/_lib';
import { LocalDateTime } from '@/components/LocalDateTime';
import {
    Sidebar,
    SidebarContent,
    SidebarGroup,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuItem,
    SidebarNavbarSpacer,
    SidebarTrigger,
    SmallSidebarPlaceholder,
    useSidebar,
} from '@/components/ui/sidebar';
import {
    HttpError,
    useBookmarks,
    useCreateBookmark,
    useDeleteBookmark,
    useHistory,
    useRenameBookmark,
} from '@/lib/calculator/use-calc-params';
import { cn } from '@/lib/css-utils';
import { IconButton } from '@/ui-lib/button';
import { Loader } from '@/ui-lib/loader';
import { Paragraph } from '@/ui-lib/paragraph';

import { BookmarkNameDialog } from './BookmarkNameDialog';
import { useTriggerCalculatorResetResultState } from './reset-results-listener';
import { buildFreightCalcUrl } from './url-helpers';

const GroupTitle = ({
    children,
    extra,
    className,
}: {
    children: React.ReactNode;
    extra?: React.ReactNode;
    className?: string;
}) => (
    <SidebarMenuItem
        className={cn('px-2 pb-2 pt-6 flex gap-2 items-center border-b border-b-sidebar-border', className)}
    >
        <Paragraph fontWeight="medium" size="xs" className="text-neutral-200 leading-6 flex-1">
            {children}
        </Paragraph>

        {extra}
    </SidebarMenuItem>
);

const historyLabel = (entry: CalcHistoryListEntry, index: number) => entry.defaultName?.trim() || `Search ${index + 1}`;

const SIDEBAR_OPEN_STORAGE_KEY = 'freight-calc-sidebar-open';
const SIDEBAR_COLLAPSE_BELOW_WIDTH = 1024; // tablet

export const useFreightSidebarOpenState = () => {
    const [open, setOpenState] = useState(true);

    useEffect(() => {
        const stored = window.localStorage.getItem(SIDEBAR_OPEN_STORAGE_KEY);
        if (stored !== null) {
            setOpenState(stored === 'true');
            return;
        }
        setOpenState(window.innerWidth >= SIDEBAR_COLLAPSE_BELOW_WIDTH);
    }, []);

    const setOpen = useCallback((value: boolean) => {
        setOpenState(value);
        window.localStorage.setItem(SIDEBAR_OPEN_STORAGE_KEY, String(value));
    }, []);

    return [open, setOpen] as const;
};

const SIDEBAR_WIDTH_STORAGE_KEY = 'freight-calc-sidebar-width';
// Keep in sync with SIDEBAR_WIDTH ('16rem') in components/ui/sidebar.tsx
const MIN_SIDEBAR_WIDTH = 256;
const MAX_SIDEBAR_WIDTH = 480;

// allows resizing sidebar using a drag handle (min/max from above apply)
const SidebarResizeHandle = ({ sidebarRef }: { sidebarRef: React.RefObject<HTMLDivElement | null> }) => {
    const { state } = useSidebar();
    const draggingRef = useRef(false);

    // keep track of where the grab started so we can keep same offset throughout the interaction
    //  so the bar doesn't "jump" on drag.
    const grabOffsetRef = useRef(0);

    if (state !== 'expanded') return null;

    const stopDragging = (event: React.PointerEvent<HTMLButtonElement>) => {
        if (!draggingRef.current) return;
        draggingRef.current = false;
        event.currentTarget.releasePointerCapture(event.pointerId);
        document.body.style.removeProperty('cursor');
        document.body.style.removeProperty('user-select');

        if (sidebarRef.current) {
            // Re-enable sidebar transitions after drag completes
            Array.from(sidebarRef.current.children).forEach((el) => {
                (el as HTMLElement).style.transition = '';
            });

            const width = sidebarRef.current.style.getPropertyValue('--sidebar-width');
            window.localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, width);
        }
    };

    return (
        <button
            type="button"
            tabIndex={-1}
            aria-label="Resize sidebar"
            title="Resize sidebar"
            onPointerDown={(event) => {
                event.preventDefault();
                draggingRef.current = true;
                event.currentTarget.setPointerCapture(event.pointerId);
                document.body.style.cursor = 'ew-resize';
                document.body.style.userSelect = 'none';

                if (sidebarRef.current) {
                    const rect = sidebarRef.current.getBoundingClientRect();
                    grabOffsetRef.current = event.clientX - rect.left - rect.width;

                    // disable sidebar transitions while drag-to-resize gesture is active
                    //
                    //  avoids "Slow" feel of the resize
                    Array.from(sidebarRef.current.children).forEach((el) => {
                        (el as HTMLElement).style.transition = 'none';
                    });
                }
            }}
            onPointerMove={(event) => {
                if (!draggingRef.current || !sidebarRef.current) return;
                const rect = sidebarRef.current.getBoundingClientRect();
                const nextWidth = Math.min(
                    MAX_SIDEBAR_WIDTH,
                    Math.max(MIN_SIDEBAR_WIDTH, event.clientX - rect.left - grabOffsetRef.current)
                );
                sidebarRef.current.style.setProperty('--sidebar-width', `${nextWidth}px`);
            }}
            onPointerUp={stopDragging}
            onPointerCancel={stopDragging}
            className="absolute inset-y-0 -right-4 z-20 hidden w-4 -translate-x-1/2 cursor-ew-resize touch-none transition-colors after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] hover:bg-neutral-800 hover:after:bg-sidebar-border sm:block"
        />
    );
};

const SidebarLoader = () => (
    <div className="w-full py-4 flex items-center justify-center">
        <Loader mode="light" size="sm" />
    </div>
);

const SidebarRow = ({
    label,
    bookmarked,
    onBookmarkClick,
    url,
    onNavigate,
    onRename,
    timestamp,
}: {
    label: string;
    /** When true, render the filled icon (bookmark list). When false, render the outlined icon (history list). */
    bookmarked: boolean;
    onBookmarkClick: () => void;
    url: string;
    onNavigate: () => void;
    onRename?: () => void;
    /** Optional ISO timestamp; rendered as a small dim line below the label. */
    timestamp?: string;
}) => {
    return (
        <div className="w-full -mx-2 px-2 py-2 flex gap-2 text-neutral-200 items-center justify-start">
            <IconButton
                icon={bookmarked ? IconBookmarkFilled : IconBookmark}
                onClick={onBookmarkClick}
                className="border-transparent hover:border-transparent focus:border-transparent shrink-0 h-4 w-4 group/btn"
                textClassName="!text-neutral-200 group-hover/btn:!text-neutral-50 group-focus/btn:!text-neutral-50"
                title={bookmarked ? 'Remove Bookmark' : 'Add Bookmark'}
            />
            <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                <Link
                    href={url}
                    onClick={onNavigate}
                    className="text-xs/4.5 text-neutral-200 group-hover/bookmark-item:text-neutral-50 truncate"
                >
                    {label}
                </Link>
                {timestamp ? (
                    <span className="text-[10px] leading-none text-neutral-300">
                        <LocalDateTime format="MMM d, HH:mm">{timestamp}</LocalDateTime>
                    </span>
                ) : null}
            </div>
            {onRename ? (
                <IconButton
                    icon={IconPencil}
                    onClick={onRename}
                    className="border-transparent hover:border-transparent focus:border-transparent shrink-0 h-4 w-4 group/btn"
                    textClassName="!text-neutral-200 group-hover/btn:!text-neutral-50 group-focus/btn:!text-neutral-50"
                    title="Rename Bookmark"
                />
            ) : null}
        </div>
    );
};

type PendingBookmark = {
    parameters: unknown;
    suggested: string;
    /** When set, the user is editing an existing bookmark surfaced by a 409. */
    existingId?: number;
};

export const FreightCalculatorSidebar = () => {
    const bookmarks = useBookmarks();
    const history = useHistory();
    const createBookmark = useCreateBookmark();
    const renameBookmark = useRenameBookmark();
    const deleteBookmark = useDeleteBookmark();
    const triggerReset = useTriggerCalculatorResetResultState();

    const bookmarkItems = bookmarks.data ?? [];
    const historyItems = history.data ?? [];

    const [pendingBookmark, setPendingBookmark] = useState<PendingBookmark | null>(null);
    const [pendingRename, setPendingRename] = useState<{ id: number; current: string } | null>(null);
    const sidebarRef = useRef<HTMLDivElement | null>(null);
    const { state } = useSidebar();
    const hasSettledRef = useRef(false);

    // Ref callback (not a useEffect) so this runs the instant the sidebar's DOM node
    // mounts
    const setSidebarRef = useCallback((node: HTMLDivElement | null) => {
        sidebarRef.current = node;
        if (!node) return;

        const stored = window.localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY);
        const storedWidth = stored ? Number.parseFloat(stored) : NaN;
        if (Number.isNaN(storedWidth)) return;

        const clamped = Math.min(MAX_SIDEBAR_WIDTH, Math.max(MIN_SIDEBAR_WIDTH, storedWidth));
        node.style.setProperty('--sidebar-width', `${clamped}px`);
    }, []);

    // Stored width/open status are not available during first render (they are only known after mount). So
    //  we block that first animation so it snaps into position instead of animating with a transition.
    useEffect(() => {
        if (hasSettledRef.current || !sidebarRef.current) return;

        const animatedEls = Array.from(sidebarRef.current.children) as HTMLElement[];
        animatedEls.forEach((el) => {
            el.style.transition = 'none';
        });

        const raf = requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                animatedEls.forEach((el) => {
                    el.style.transition = '';
                });
                hasSettledRef.current = true;
            });
        });

        return () => cancelAnimationFrame(raf);
    }, [state]);

    const onAddBookmarkFromHistory = (entry: CalcHistoryListEntry, index: number) => {
        setPendingBookmark({
            parameters: entry.parameters,
            suggested: historyLabel(entry, index),
        });
    };

    const onRemoveBookmark = (entry: CalcBookmarkListEntry) => {
        deleteBookmark.mutate(entry.id);
    };

    const confirmBookmark = (name: string) => {
        if (!pendingBookmark) return;

        // If the dialog was opened to edit an existing bookmark (409 surfaced
        // mid-create), patch the existing row instead of re-creating.
        if (pendingBookmark.existingId !== undefined) {
            renameBookmark.mutate({ id: pendingBookmark.existingId, name });
            setPendingBookmark(null);
            return;
        }

        createBookmark.mutate(
            { parameters: pendingBookmark.parameters, name },
            {
                onSuccess: () => setPendingBookmark(null),
                onError: (error) => {
                    if (error instanceof HttpError && error.status === 409) {
                        if (error.body?.code === 'cap') {
                            toast.error('Bookmark limit reached', {
                                description: `You have hit the limit of ${error.body.limit} bookmarks. Remove some before adding new ones.`,
                            });
                            setPendingBookmark(null);
                            return;
                        }
                        if (error.body?.code === 'duplicate' && error.body.entry) {
                            // Pivot the dialog to rename the pre-existing bookmark.
                            setPendingBookmark({
                                parameters: pendingBookmark.parameters,
                                suggested: error.body.entry.name,
                                existingId: error.body.entry.id,
                            });
                            return;
                        }
                    }
                    // Other failures: leave dialog open so user can retry.
                },
            }
        );
    };

    const onRename = (entry: CalcBookmarkListEntry) => {
        setPendingRename({ id: entry.id, current: entry.name });
    };

    const confirmRename = (name: string) => {
        if (!pendingRename) return;
        renameBookmark.mutate({ id: pendingRename.id, name });
        setPendingRename(null);
    };

    const dialogTitle = pendingBookmark?.existingId ? 'Bookmark already exists' : 'Save Bookmark';
    const dialogDescription = pendingBookmark?.existingId
        ? 'You have already bookmarked this calculation. Update its name below.'
        : 'Give this saved calculation a name to find it later.';
    const dialogSubmit = pendingBookmark?.existingId ? 'Rename' : 'Save';

    return (
        <>
            <Sidebar ref={setSidebarRef} className="bg-neutral-950">
                <SidebarNavbarSpacer />
                <SidebarHeader className="p-0">
                    <SidebarMenu>
                        <GroupTitle className="pt-4" extra={<SidebarTrigger className="self-end" />}>
                            Saved Bookmarks
                        </GroupTitle>
                    </SidebarMenu>
                </SidebarHeader>
                <SidebarContent className="gap-0">
                    <div className="pl-2 pr-0 flex flex-col items-center justify-center">
                        {bookmarks.isPending ? (
                            <SidebarLoader />
                        ) : bookmarkItems.length > 0 ? (
                            bookmarkItems.map((entry) => (
                                <SidebarRow
                                    key={entry.id}
                                    label={entry.name}
                                    url={buildFreightCalcUrl(entry.parameters)}
                                    bookmarked
                                    onBookmarkClick={() => onRemoveBookmark(entry)}
                                    onNavigate={triggerReset}
                                    onRename={() => onRename(entry)}
                                />
                            ))
                        ) : (
                            <div className="text-neutral-300 py-4 pb-2">No bookmarks available</div>
                        )}
                    </div>

                    <SidebarGroup className="p-0">
                        <SidebarMenu className="gap-0">
                            <GroupTitle className={bookmarkItems.length === 0 ? 'pt-0' : 'pt-4'}>
                                Search History
                            </GroupTitle>

                            <div className="pl-2 pr-0 flex flex-col items-center justify-center">
                                {history.isPending ? (
                                    <SidebarLoader />
                                ) : historyItems.length > 0 ? (
                                    historyItems.map((entry, idx) => (
                                        <SidebarRow
                                            key={entry.id}
                                            label={historyLabel(entry, idx)}
                                            url={buildFreightCalcUrl(entry.parameters)}
                                            bookmarked={false}
                                            onBookmarkClick={() => onAddBookmarkFromHistory(entry, idx)}
                                            onNavigate={triggerReset}
                                            timestamp={entry.createdAt}
                                        />
                                    ))
                                ) : (
                                    <div className="text-neutral-300 py-4">No search history available</div>
                                )}
                            </div>
                        </SidebarMenu>
                    </SidebarGroup>
                </SidebarContent>

                <SmallSidebarPlaceholder>
                    <SidebarNavbarSpacer />
                    <SidebarHeader className="p-0">
                        <SidebarMenuItem className="px-2 pb-2  flex gap-2 items-center border-b border-b-sidebar-border">
                            <SidebarTrigger />
                        </SidebarMenuItem>
                    </SidebarHeader>
                </SmallSidebarPlaceholder>

                <SidebarResizeHandle sidebarRef={sidebarRef} />
            </Sidebar>

            <BookmarkNameDialog
                open={pendingBookmark !== null}
                defaultValue={pendingBookmark?.suggested ?? ''}
                onConfirm={confirmBookmark}
                onCancel={() => setPendingBookmark(null)}
                title={dialogTitle}
                description={dialogDescription}
                submitLabel={dialogSubmit}
            />

            <BookmarkNameDialog
                open={pendingRename !== null}
                defaultValue={pendingRename?.current ?? ''}
                onConfirm={confirmRename}
                onCancel={() => setPendingRename(null)}
                title="Rename Bookmark"
                description="Update the name for this saved calculation."
                submitLabel="Rename"
            />
        </>
    );
};
